// src/components/molecules/InteractionItem.js

import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "styled-components";
import PropTypes from "prop-types";
import { getStorage, ref as storageRef, deleteObject } from "firebase/storage";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";

const InteractionItem = ({ interaction, videoId, onDelete }) => {
  const theme = useTheme();
  const [deleting, setDeleting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false); // State for confirmation dialog
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  }); // State for snackbar notifications
  const storage = getStorage();

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = async () => {
    setDeleting(true);

    try {
      // Delete from Firestore
      const interactionDocRef = doc(
        db,
        "videos",
        videoId,
        "interactions",
        interaction.id
      );
      await deleteDoc(interactionDocRef);

      // Delete media from Firebase Storage if mediaURL exists
      if (interaction.mediaURL) {
        const url = new URL(interaction.mediaURL);
        const decodedPath = decodeURIComponent(
          url.pathname.split("/o/")[1].split("?")[0]
        );
        const mediaRef = storageRef(storage, decodedPath);
        await deleteObject(mediaRef);
      }

      // Notify parent to remove from state
      onDelete(interaction.id);

      // Show success snackbar
      setSnackbar({
        open: true,
        message: "Interaction deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting interaction:", error);
      // Show error snackbar
      setSnackbar({
        open: true,
        message: "Failed to delete interaction. Please try again.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setOpenConfirm(false); // Close the confirmation dialog
    }
  };

  // Helper function to format time
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  return (
    <Box mb={2}>
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          backgroundColor: theme.colors.mediumPurple,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* First Column: Image/Video Thumbnail */}
          <Grid item xs={12} sm={3} md={2}>
            {interaction.mediaURL && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                {interaction.mediaType === "image" ? (
                  <img
                    src={interaction.mediaURL}
                    alt={interaction.title}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <video
                    src={interaction.mediaURL}
                    controls
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            )}
          </Grid>

          {/* Second Column: Title, Description, and CTA Button */}
          <Grid item xs={12} sm={6} md={8}>
            <Typography
              variant="subtitle1"
              component="div"
              gutterBottom
              sx={{ color: "white" }}
            >
              <b>{interaction.title}</b> at {formatTime(interaction.timestamp)}
            </Typography>
            <Typography variant="body2" sx={{ color: "white" }}>
              {interaction.description}
            </Typography>
            {/* CTA Button Placed Below Description */}
            {interaction.callToAction && (
              <Box mt={1}>
                <a
                  href={interaction.callToActionURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                      borderRadius: "50px",
                      padding: "10px 30px",
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: theme.colors.green,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {interaction.callToAction}
                  </Button>
                </a>
              </Box>
            )}
          </Grid>

          {/* Third Column: Delete Button */}
          <Grid item xs={12} sm={3} md={2} container justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleOpenConfirm}
              disabled={deleting}
              sx={{
                backgroundColor: theme.colors.red,
                color: theme.colors.white,
                borderRadius: "50px",
                padding: "10px 30px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.colors.red,
                  boxShadow: "none",
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={24} sx={{ color: theme.colors.red }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <DialogTitle
          id="confirm-delete-title"
          sx={{
            fontFamily: "basic-sans",
            fontWeight: "700",
            backgroundColor: theme.colors.headerPurple,
            color: "white",
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "basic-sans",
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
          }}
        >
          <DialogContentText
            id="confirm-delete-description"
            sx={{
              fontFamily: "basic-sans",
              color: theme.colors.lightGray,
              pt: 2,
            }}
          >
            Are you sure you want to delete the interaction "{interaction.title}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: theme.colors.mediumPurple,
            pr: 2,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCloseConfirm}
            color="primary"
            sx={{
              backgroundColor: theme.colors.darkGray,
              color: theme.colors.lightGray,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.lightGray,
                color: theme.colors.darkGray,
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            autoFocus
            sx={{
              backgroundColor: theme.colors.red,
              color: theme.colors.white,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.red,
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

InteractionItem.propTypes = {
  interaction: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    mediaURL: PropTypes.string,
    mediaType: PropTypes.oneOf(["image", "video"]),
    callToAction: PropTypes.string,
    callToActionURL: PropTypes.string,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
  videoId: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default InteractionItem;
